/**
 * TanStack Query (React Query) v5 Configuration
 *
 * Central configuration for React Query client with default cache settings,
 * retry logic, and background refetching behavior.
 *
 * @see https://tanstack.com/query/v5/docs/reference/QueryClient
 */

import { QueryClient } from '@tanstack/react-query'

/**
 * Create QueryClient with optimized defaults:
 * - Cache time: 5 minutes (data stays in cache for 5 minutes after being unused)
 * - Stale time: 5 minutes for budget endpoints, 1 minute for others (AC: 1)
 * - Retry: 3 attempts with exponential backoff
 * - Refetch on window focus: Enabled (keeps data fresh when user returns to tab)
 * - Refetch on reconnect: Enabled (syncs data when network reconnects)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data remains in cache for 5 minutes after being unused
      gcTime: 5 * 60 * 1000, // 5 minutes (renamed from cacheTime in v5)

      // Data considered fresh for 5 minutes (no background refetch during this time)
      // This is the default for budget endpoints per AC1, other modules can override
      staleTime: 5 * 60 * 1000, // 5 minutes (updated from 1 minute)

      // Automatically refetch stale data when window regains focus
      refetchOnWindowFocus: true,

      // Automatically refetch when network reconnects
      refetchOnReconnect: true,

      // Number of retry attempts for failed queries
      retry: 3,

      // Exponential backoff for retries: 1s, 2s, 4s (max 30s)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,

      // Exponential backoff for mutation retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  }
})
